import Button from "@/common/components/Button";
import { FileUploader } from "@/common/components/FileUploader";
import Modal from "@/common/components/Modal";
import { checkIsXml } from "@/common/utils/fileValidator";
import { useRef, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import useBillingFiscalDocumentGroupUploadFiscalDocument from "../../hooks/useBillingFiscalDocumentGroupUploadFiscalDocument";
import Checkbox from "@/common/components/Checkbox";

const UploadFiscalDocument = ({ groupId }: { groupId: string; }) => {
  const queryClient = useQueryClient();

  const [skipProductValidations, setSkipProductValidations] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [file, setFile] = useState<Blob>();
  const [fileName, setFileName] = useState('');

  const uploadFiscalDocumentRef = useRef(null);

  const clearFile = () => {
    setFile(undefined);
    setFileName('');
  };

  const { mutate: uploadFiscalDocumentForConsumption } = useBillingFiscalDocumentGroupUploadFiscalDocument({
    id: groupId,
    onSuccess: () => {
      clearFile();
      setModalIsOpen(false);
      setIsError(false);
      setMessages(["Nota enviada com sucesso!"]);
    },
    onError: (error) => {
      if (error.response?.data.errors) {
        clearFile();
        setModalIsOpen(false);
        setIsError(true);
        setMessages(Object.values(error.response?.data.errors).flat());
      }
    }
  });

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={(value) => {
          setModalIsOpen(value);
          clearFile();
        }}
        Trigger={(
          <Button>Upload de nota</Button>
        )}
        title="Nova nota fiscal"
        footer="Confirmar upload manual"
        footerClassName="bg-primary-blue text-white-800"
        onConfirm={() => file && uploadFiscalDocumentForConsumption({file, skipProductValidations})}
        body={(
          <div className="flex flex-col gap-y-4">
            <FileUploader
              ref={uploadFiscalDocumentRef}
              className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
              title="Anexe a nota fiscal"
              id="upload_fiscal_document"
              accept="application/xml"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement & { files: FileList; }).files?.[0];

                if (!file || !checkIsXml(file)) {
                  return;
                }

                setFileName(file.name);
                setFile(file);
              }}
              fileName={fileName ? fileName : undefined}
              currentFileUrl={file ? URL.createObjectURL(file) : undefined}
            />

            <Checkbox
              label="Ignorar validações de produto e valor"
              className="text-white-800"
              checked={skipProductValidations}
              onChange={() => setSkipProductValidations(!skipProductValidations)}
            />
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingFiscalDocumentGroup', groupId] });
            queryClient.invalidateQueries({ queryKey: ['billingFiscalDocumentGroups'] });
          }}
        />
      )}
    </>
  );
};

export default UploadFiscalDocument;
